import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import type { IntentType, ABTest, ContentType, CustomAxis } from "@/types/ab-tests";
import { ActivityLogTimeline } from "../ActivityLogTimeline";
import { TestMetaRow } from "./TestMetaRow";
import { ListingFieldsSelector } from "./ListingFieldsSelector";
import { IntentShiftSelector } from "./IntentShiftSelector";
import { KeywordSwapSelector } from "./KeywordSwapSelector";
import { VariantContentEditor } from "./VariantContentEditor";
import { CustomAxesSelector } from "./CustomAxesSelector";

interface EditTestFormProps {
  test: ABTest;
  bulletPoints: string[];
  productId?: number;
  resolvedBrandId: number | null | undefined;
  selectedFields: ContentType[];
  onToggleField: (field: ContentType) => void;
  showIntent: boolean;
  showKeyword: boolean;
  showCustom: boolean;
  intentFrom: IntentType;
  intentTo: IntentType;
  onIntentFromChange: (v: IntentType) => void;
  onIntentToChange: (v: IntentType) => void;
  keywordOptions: string[];
  keywordToRemove: string;
  targetPhrase: string;
  onKeywordToRemoveChange: (v: string) => void;
  onTargetPhraseChange: (v: string) => void;
  customAxes: CustomAxis[];
  onToggleCustomAxis: (axis: CustomAxis) => void;
  hypothesis: string;
  onHypothesisChange: (v: string) => void;
  variantATitle: string;
  variantABullets: Record<string, string>;
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  onVariantBTitleChange: (v: string) => void;
  onVariantBBulletChange: (field: string, value: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSaving: boolean;
}

export function EditTestForm({
  test,
  bulletPoints,
  productId,
  resolvedBrandId,
  selectedFields,
  onToggleField,
  showIntent,
  showKeyword,
  showCustom,
  intentFrom,
  intentTo,
  onIntentFromChange,
  onIntentToChange,
  keywordOptions,
  keywordToRemove,
  targetPhrase,
  onKeywordToRemoveChange,
  onTargetPhraseChange,
  customAxes,
  onToggleCustomAxis,
  hypothesis,
  onHypothesisChange,
  variantATitle,
  variantABullets,
  variantBTitle,
  variantBBullets,
  onVariantBTitleChange,
  onVariantBBulletChange,
  isGenerating,
  onGenerate,
  onCancel,
  onSubmit,
  isSaving,
}: EditTestFormProps) {
  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-5 py-2">
          <TestMetaRow test={test} />
          <ListingFieldsSelector
            availableBulletPoints={bulletPoints}
            selectedFields={selectedFields}
            onToggle={onToggleField}
          />
          {showIntent && (
            <IntentShiftSelector
              intentFrom={intentFrom}
              intentTo={intentTo}
              onIntentFromChange={onIntentFromChange}
              onIntentToChange={onIntentToChange}
            />
          )}
          {showKeyword && (
            <KeywordSwapSelector
              keywordOptions={keywordOptions}
              keywordToRemove={keywordToRemove}
              targetPhrase={targetPhrase}
              onKeywordToRemoveChange={onKeywordToRemoveChange}
              onTargetPhraseChange={onTargetPhraseChange}
            />
          )}
          {showCustom && (
            <CustomAxesSelector customAxes={customAxes} onToggle={onToggleCustomAxis} />
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-hypothesis">Hypothesis</Label>
            <Textarea
              id="edit-hypothesis"
              placeholder="Enter your hypothesis..."
              value={hypothesis}
              onChange={(e) => onHypothesisChange(e.target.value)}
              rows={3}
              className="resize-none bg-muted/50"
            />
          </div>
          <VariantContentEditor
            selectedFields={selectedFields}
            variantATitle={variantATitle}
            variantABullets={variantABullets}
            variantBTitle={variantBTitle}
            variantBBullets={variantBBullets}
            onVariantBTitleChange={onVariantBTitleChange}
            onVariantBBulletChange={onVariantBBulletChange}
            isGenerating={isGenerating}
            onGenerate={onGenerate}
          />
        </div>

        {test.activityLog && test.activityLog.length > 0 && (
          <div className="border-t pt-4">
            <ActivityLogTimeline
              entries={test.activityLog}
              showHeader={true}
              productId={productId}
              brandId={resolvedBrandId ?? undefined}
              testId={test.id}
            />
          </div>
        )}
      </div>

      <SheetFooter className="px-6 py-4 border-t border-border bg-card">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={isSaving}>
          {isSaving ? "Saving…" : "Save Changes"}
        </Button>
      </SheetFooter>
    </>
  );
}
